import React, { useCallback, useEffect, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';

import { toUiTier } from '../adapters';
import type {
  AuditLogEntry, BenefitCode, Tier as CrmTier, TierBenefitLink, TierBenefitLinkPatch,
} from '../crmClient';
import { toAuditEntries, type AuditLookup } from '../adapters';
import type { AuditEntry } from '../../components';
import type { BenefitCatalogItem } from '../../screens/loyaltyData';
import { Icon } from '../../components/base/Icon';
import { primaryBtnCls, ScreenSkeleton } from '../../components';
import { TierScreen } from '../../screens/TierScreen';
import { TIERS as MOCK_TIERS } from '../../screens/loyaltyData';
import type { Tier, TierBenefit } from '../../screens/loyaltyData';

/**
 * ต่อหน้าระดับสมาชิกเข้ากับ CRM module
 *
 * ⚠️ ข้อจำกัดที่ตกลงกันแล้วในรอบนี้ (ดู เอกสารวิเคราะห์ช่องว่างของ API ที่ทีมเก็บไว้ภายใน ข้อ 2ง)
 *    `benefit_codes` เป็น readOnly ทั้งคู่ → **ผูกสิทธิ์เข้าระดับผ่าน API ไม่ได้**
 *    ผูกผ่านหน้าผู้ดูแลระบบของ backend แทน แล้วหน้านี้แสดงผลอย่างเดียว
 *    สิ่งที่บันทึกกลับได้จริงคือ **ชื่อระดับ กับ เกณฑ์ยอดขั้นต่ำ** เท่านั้น
 */

/** ปุ่มสร้างระดับตั้งต้นไม่มีกล่องให้กรอกเหตุผล — ใส่ให้เองเพื่อให้ประวัติอ่านออกว่ามาจากไหน */
const DEFAULT_TIERS_REASON = 'สร้างระดับตั้งต้น 3 ระดับตาม SRS 3.4 (Silver · Gold · Platinum)';

export function TierContainer() {
  const { api, dataMode, linkComponent, divisionId } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [tiers, setTiers] = useState<Tier[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  /** จำนวนสมาชิกจริงต่อระดับ ตามเกณฑ์ที่ใช้อยู่ — คีย์เป็น id ของระดับในหน้าจอ */
  const [memberCounts, setMemberCounts] = useState<Map<string, number> | undefined>(undefined);
  /** รหัสสิทธิ์ที่เลือกได้ — มาจากตารางแม่ของ backend ไม่ใช่ค่าที่พิมพ์เอง */
  const [benefitCatalog, setBenefitCatalog] = useState<BenefitCatalogItem[] | undefined>(undefined);
  /** ประวัติการแก้ระดับและสิทธิ์ — ของเสริม ล้มแล้วหน้ายังใช้งานได้ */
  const [auditEntries, setAuditEntries] = useState<AuditEntry[] | undefined>(undefined);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');
    Promise.all([
      api.listTiers(),
      /**
       * ⚠️ loyalty-tier-summary **ไม่ห่อ pagination** ตอบเป็น array ตรง ๆ
       *    และเป็นของเสริม ถ้าล้มเหลวไม่ควรทำให้ทั้งหน้าพัง
       */
      api.listTierSummary().catch(() => null),
      /* รหัสสิทธิ์กลาง ใช้แปลง id ในตารางเชื่อมเป็นชื่อ และเป็นตัวเลือกตอนเพิ่มสิทธิ์ */
      api.listBenefitCodes().catch(() => null),
      /**
       * ประวัติสองชุดรวมกัน — การแก้ระดับ กับ การผูก/ถอดสิทธิ์
       * backend แยก endpoint เพราะเป็นคนละข้อมูล แต่ผู้ใช้มองเป็นเรื่องเดียวกัน
       */
      Promise.all([
        api.listAuditLogs('tier').catch(() => null),
        api.listAuditLogs('tier-benefit-code').catch(() => null),
      ]).catch(() => [null, null] as const),
    ])
      .then(async ([page, summary, codes, auditPair]) => {
        if (cancelled) return;

        /**
         * โหลดสิทธิประโยชน์ของแต่ละระดับจาก endpoint ซ้อน
         *
         * ⚠️ ยิงทีละระดับเพราะ backend ผูก policy_id ไว้ใน path ไม่มีตัวกรองรวม
         *    ระดับมีแค่ 3–5 รายการ จึงยอมรับได้ ถ้าวันหนึ่งมีเป็นสิบให้ขอ backend
         *    เพิ่ม endpoint รวม หรือให้ข้อมูลระดับส่งความสัมพันธ์มาด้วยเลย
         *
         *    ถ้าระดับไหนโหลดไม่ได้ ให้เป็น undefined แล้วถอยไปใช้ active_benefit_codes
         *    ของระดับนั้น — ดีกว่าทำให้ทั้งหน้าพังเพราะสิทธิ์ระดับเดียว
         */
        const linkLists = await Promise.all(
          page.items.map((t: CrmTier) =>
            api.listTierBenefits(t.id).then((r) => r.items).catch(() => undefined),
          ),
        );
        if (cancelled) return;

        const codeById = new Map((codes?.items ?? []).map((c) => [c.id, c]));

        setBenefitCatalog(
          codes
            ? codes.items.map((c) => ({
                codeId: String(c.id),
                code: c.code,
                text: c.name || c.description || c.code,
              }))
            : undefined,
        );

        const [tierAudit, benefitAudit] = auditPair ?? [null, null];
        const tierLogs = tierAudit?.items ?? [];
        const merged = [...tierLogs, ...(benefitAudit?.items ?? [])];
        setAuditEntries(
          tierAudit || benefitAudit ? toAuditEntries(merged, auditLookup(page.items, codeById, tierLogs)) : undefined,
        );

        setTiers(page.items.map((t: CrmTier, i) => toTierScreenShape(t, linkLists[i], codeById)));
        setMemberCounts(
          summary
            ? new Map(summary.map((s) => [String(s.tier.id), s.totalAccounts]))
            : new Map(),
        );
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  /**
   * บันทึกกลับ — เทียบด้วย **id ของ backend** แล้วส่งเฉพาะที่เปลี่ยนจริง
   * ระดับใหม่ใช้ POST · ระดับเดิมใช้ PATCH · ที่หายไปใช้ DELETE
   *
   * ⚠️ ลำดับสำคัญ: **สร้างและแก้ก่อน แล้วค่อยลบ**
   *    ถ้าลบระดับฐาน (0 บาท) ก่อนแล้วค่อยตั้งระดับอื่นเป็น 0 จะมีช่วงที่สมาชิกบางคนไม่อยู่ระดับใดเลย
   *    backend ไม่ห้ามเกณฑ์ซ้ำ ช่วงที่มีสองระดับเป็น 0 ชั่วคราวจึงไม่ผิด
   *
   * เหตุผลจากกล่องยืนยันไปกับ**ทุก**คำขอในรอบนี้ — การบันทึกครั้งเดียวกลายเป็นหลายแถวใน Audit Log
   * ทุกแถวต้องตอบได้ว่าแก้เพราะอะไร
   */
  const onSave = useCallback(async (next: Tier[], changeReason?: string) => {
    if (isMock) return;
    const current = await api.listTiers();
    const currentById = new Map(current.items.map((t) => [String(t.id), t]));

    /** id ชั่วคราวของระดับที่เพิ่งเพิ่ม → id จริงหลังสร้าง ใช้ผูกสิทธิ์ต่อด้านล่าง */
    const createdId = new Map<string, number>();

    for (const t of next) {
      const existing = currentById.get(t.id);
      const startPoint = String(t.minSpend);
      if (!existing) {
        const created = await api.createTier(t.name.trim(), startPoint, changeReason);
        createdId.set(t.id, created.id);
      } else if (existing.name !== t.name.trim() || Number(existing.startPoint) !== t.minSpend) {
        await api.updateTier(existing.id, { name: t.name.trim(), startPoint, changeReason });
      }
    }

    /* ระดับที่ไม่มีในชุดใหม่แล้ว = ผู้ใช้ลบออก · backend ลบการผูกสิทธิ์ของระดับนั้นให้เอง */
    const keep = new Set(next.map((t) => t.id));
    for (const gone of current.items) {
      if (!keep.has(String(gone.id))) await api.deleteTier(gone.id, changeReason);
    }

    /**
     * บันทึกสิทธิประโยชน์ของแต่ละระดับผ่าน endpoint ซ้อน
     *
     * สิ่งที่บันทึกได้จริง — เทียบกับความสามารถของ backend ตรง ๆ
     *   ✅ แก้เพดานต่อคน (capPerYear → per_user_limit)
     *   ✅ ถอดสิทธิ์ออกจากระดับ (unlink)
     *   ⛔ เพิ่มสิทธิ์ใหม่จากข้อความที่พิมพ์เอง — ทำไม่ได้
     *
     * ⛔ เพราะ `LoyaltyBenefitCode.code` เป็น enum ตายตัว 7 ค่า (D03 D05 D10 D15 D20 D25 FS)
     *    และ backend มี sync_benefit_codes() คอยซิงก์ตารางแม่กับ enum
     *    การสร้างรหัสใหม่จึงเป็นการแก้โค้ดฝั่ง backend ไม่ใช่งานของหน้าจอ
     *    → ถ้าผู้ใช้เพิ่มรายการใหม่ ต้องบอกให้ชัดว่าทำไมบันทึกไม่ได้
     *      ดีกว่าเงียบแล้วปล่อยให้เขาคิดว่าบันทึกสำเร็จ
     */
    for (const t of next) {
      const tierId = createdId.get(t.id) ?? (currentById.has(t.id) ? Number(t.id) : undefined);
      if (tierId === undefined) continue;

      /* ระดับที่เพิ่งสร้างยังไม่มีการผูกสิทธิ์ ไม่ต้องโหลดของเดิม */
      const links = createdId.has(t.id)
        ? []
        : await api.listTierBenefits(tierId).then((r) => r.items).catch(() => null);
      if (!links) continue; // โหลดของเดิมไม่ได้ ไม่กล้าบันทึกทับ

      const linkById = new Map(links.map((l) => [String(l.id), l]));

      for (const b of t.benefits) {
        const link = linkById.get(b.id);

        if (!link) {
          /**
           * รายการที่ผู้ใช้เพิ่งเลือกเพิ่ม — หน้าจอตั้ง id เป็น 'new-<codeId>'
           * ผูกเข้าระดับด้วยรหัสที่เลือก แล้วตั้งเพดานต่อคนไปในครั้งเดียว
           */
          await api.linkTierBenefit(tierId, {
            benefitCode: Number(b.codeId),
            perUserLimit: b.capPerYear,
            hasClaimButton: b.claimable,
            changeReason,
          });
          continue;
        }

        /* ส่งเฉพาะค่าที่เปลี่ยนจริง — ทั้งสองค่าอยู่แถวเดียวกัน แต่ส่งแยกกันได้ */
        const patch: TierBenefitLinkPatch = {};
        if ((link.perUserLimit ?? null) !== b.capPerYear) patch.perUserLimit = b.capPerYear;
        if ((link.hasClaimButton !== false) !== b.claimable) patch.hasClaimButton = b.claimable;
        if (Object.keys(patch).length > 0) {
          await api.updateTierBenefit(tierId, link.id, { ...patch, changeReason });
        }
        linkById.delete(b.id);
      }

      // ที่เหลือใน map = ผู้ใช้ถอดออกจากระดับนี้
      for (const gone of linkById.values()) await api.unlinkTierBenefit(tierId, gone.id, changeReason);
    }

    const fresh = await api.listTiers();
    const freshLinks = await Promise.all(
      fresh.items.map((t: CrmTier) =>
        api.listTierBenefits(t.id).then((r) => r.items).catch(() => undefined),
      ),
    );
    const freshCodes = await api.listBenefitCodes().catch(() => null);
    const codeById = new Map((freshCodes?.items ?? []).map((c) => [c.id, c]));

    setTiers(fresh.items.map((t: CrmTier, i) => toTierScreenShape(t, freshLinks[i], codeById)));

    /* ประวัติเปลี่ยนทุกครั้งที่บันทึก — ดึงใหม่เพื่อให้ผู้ใช้เห็นรายการที่เพิ่งทำ */
    const [tierAudit, benefitAudit] = await Promise.all([
      api.listAuditLogs('tier').catch(() => null),
      api.listAuditLogs('tier-benefit-code').catch(() => null),
    ]);
    if (tierAudit || benefitAudit) {
      const tierLogs = tierAudit?.items ?? [];
      setAuditEntries(toAuditEntries(
        [...tierLogs, ...(benefitAudit?.items ?? [])],
        auditLookup(fresh.items, codeById, tierLogs),
      ));
    }
  }, []);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  /**
   * ⚠️ TierScreen สมมติว่ามีอย่างน้อยหนึ่งระดับเสมอ (อ่าน draft[0].minSpend ตรง ๆ)
   *    division 545 ยังไม่มีระดับสักตัว ถ้าส่ง array ว่างเข้าไปหน้าจะพังทันที
   *    จึงต้องดักไว้ที่นี่แล้วแสดงหน้าตั้งต้นแทน — เจอจริงตอนต่อ CRM ครั้งแรก
   */
  const createDefaults = useCallback(async () => {
    setCreating(true);
    setCreateError(null);
    try {
      /* ค่าตั้งต้นตาม SRS 3.4 — ปรับได้ในหน้าจอหลังจากสร้างแล้ว */
      await api.createTier('Silver', '0', DEFAULT_TIERS_REASON);
      await api.createTier('Gold', '5000', DEFAULT_TIERS_REASON);
      await api.createTier('Platinum', '20000', DEFAULT_TIERS_REASON);
      const fresh = await api.listTiers();
      setTiers(fresh.items.map((t: CrmTier) => toTierScreenShape(t)));
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'สร้างไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  }, []);

  if (isMock) return <TierScreen initialState="ready" linkComponent={linkComponent} />;

  /**
   * ⚠️ ห้ามส่งหน้าจอออกไปก่อนได้ข้อมูลจริง — หน้านี้เป็นฟอร์ม ไม่มีโครงโหลดในตัว
   *    ถ้าปล่อยผ่าน หน้าจะขึ้น**เกณฑ์ยอดซื้อจำลอง** ซึ่งอ่านเหมือนค่าจริง
   *    และถ้าเผลอกดบันทึกตอนนั้นจะเขียนทับเกณฑ์จริงของ division
   */
  if (state === 'loading' && !tiers) {
    return <ScreenSkeleton title="ระดับสมาชิกและสิทธิประโยชน์" blocks={3} />;
  }

  /**
   * ⚠️ ห้ามเช็ก `state === 'ready'` ร่วมด้วย — host ใช้ React 16 ซึ่งไม่รวม setState ใน promise
   *    `setTiers([])` จึง render ทันทีก่อน `setState('ready')` แล้ว array ว่างหลุดเข้า TierScreen
   *    เจอจริงตอนสลับไป division ที่ยังไม่มีระดับ — หน้าจอขาวทั้งหน้า
   */
  if (tiers && tiers.length === 0) {
    return <NoTiersYet onCreate={createDefaults} busy={creating} error={createError} divisionId={divisionId} />;
  }

  return (
    <TierScreen
      initialState={state}
      tiers={tiers}
      onSave={onSave}
      memberCounts={memberCounts}
      benefitCatalog={benefitCatalog ?? []}
      auditEntries={auditEntries ?? []}
      linkComponent={linkComponent}
      capLabel="เพดานต่อคนต่อรอบ"
      showClaimedCount={false}
      /* ระบบหลังบ้านยังไม่มีที่เก็บภาพของระดับสมาชิก — เปิดไว้จะกลายเป็นอัปโหลดแล้วหายเงียบ */
      showImage={false}
    />
  );
}

/**
 * หน้าตั้งต้นเมื่อ division ยังไม่มีระดับสมาชิก
 *
 * บอกตรง ๆ ว่าทำไมว่าง และให้ทางไปต่อในคลิกเดียว ดีกว่าปล่อยหน้าว่าง
 * หรือให้จอพังซึ่งดูเหมือนระบบเสีย
 */
function NoTiersYet({ onCreate, busy, error, divisionId }: {
  onCreate: () => void; busy: boolean; error: string | null; divisionId: number;
}) {
  return (
    <main className="grid min-h-[70dvh] place-items-center p-[var(--space-8)]">
      <div className="max-w-[520px] text-center">
        <Icon name="workspace_premium" size={40} className="text-[var(--ink-400)]" />
        <h1 className="font-thai text-section leading-heading mt-[var(--space-3)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
          ยังไม่มีระดับสมาชิกใน division นี้
        </h1>
        <p className="font-thai text-body leading-thai mt-[var(--space-2)] text-[var(--ink-700)]">
          CRM ยังไม่มีระเบียนระดับสมาชิกสำหรับ division นี้ ({divisionId})
          จึงยังตั้งเกณฑ์ยอดซื้อและดูสิทธิประโยชน์ไม่ได้
        </p>

        <button
          type="button"
          onClick={onCreate}
          disabled={busy}
          className={`${primaryBtnCls} mt-[var(--space-6)]`}
        >
          {busy ? 'กำลังสร้าง…' : 'สร้าง 3 ระดับตั้งต้น (Silver / Gold / Platinum)'}
        </button>

        {error && (
          <p role="alert" className="font-thai text-form leading-form mt-[var(--space-3)] text-[var(--error-text)]">
            {error}
          </p>
        )}

        <p className="font-thai text-caption leading-thai mt-[var(--space-5)] text-[var(--ink-600)]">
          เกณฑ์ตั้งต้น 0 / 5,000 / 20,000 บาท — แก้ได้ในหน้าจอหลังสร้างแล้ว
          <br />
          สิทธิประโยชน์ของแต่ละระดับตั้งได้หลังจากสร้างระดับสมาชิกแล้ว
        </p>
      </div>
    </main>
  );
}

/**
 * ชื่อสำหรับประวัติการแก้ไข — ประวัติการผูกสิทธิ์เก็บแค่เลข id ของระดับและรหัสสิทธิ์
 *
 * ชื่อระดับเอาจากประวัติของระดับด้วย เพื่อให้ระดับที่ถูกลบไปแล้วยังแสดงเป็นชื่อได้
 * (รายการระดับปัจจุบันไม่มีตัวที่ลบแล้ว) · ชื่อปัจจุบันมาทีหลังจึงทับชื่อเก่า
 */
function auditLookup(tiers: CrmTier[], codeById: Map<number, BenefitCode>, tierLogs: AuditLogEntry[]): AuditLookup {
  const tierById = new Map<number, string>();
  for (const log of tierLogs) {
    const name = (log.after?.name ?? log.before?.name) as unknown;
    if (typeof name === 'string' && name && !tierById.has(log.objectId)) tierById.set(log.objectId, name);
  }
  for (const t of tiers) tierById.set(t.id, t.name);

  return {
    tierName: (id) => tierById.get(id),
    benefitName: (id) => {
      const c = codeById.get(id);
      return c ? c.name || c.description || c.code : undefined;
    },
  };
}

/** แปลง CRM tier เป็นรูปแบบที่ TierScreen ใช้ */
function toTierScreenShape(
  t: CrmTier,
  /** ความสัมพันธ์ระดับ↔สิทธิ์ จาก endpoint ซ้อน — ไม่มี = ยังโหลดไม่ได้ */
  links?: TierBenefitLink[],
  /** รหัสสิทธิ์กลาง ใช้หาชื่อจาก id ที่ตารางเชื่อมอ้างถึง */
  codeById?: Map<number, BenefitCode>,
): Tier {
  const ui = toUiTier(t);

  /**
   * ✅ ใช้ตารางเชื่อมเป็นแหล่งความจริงเมื่อโหลดได้
   *    ทำให้ได้ `per_user_limit` มาเป็นเพดานต่อคนจริง ไม่ใช่ null ตายตัวเหมือนเดิม
   *    (ข้อจำกัดเดิมที่บันทึกไว้ว่า "API ไม่ส่ง per_user_limit ออกมา" มาจากการดูแค่
   *     ฟิลด์ active_benefit_codes ของ tier ซึ่งเป็น readOnly — endpoint ซ้อนส่งมาให้ครบ)
   *
   *    ถ้าโหลดตารางเชื่อมไม่ได้ ถอยไปใช้ active_benefit_codes เหมือนเดิม
   */
  const benefits: TierBenefit[] = links
    ? links.map((link) => {
        const code = codeById?.get(link.benefitCode);
        return {
          /* ใช้ id ของ "ความสัมพันธ์" ไม่ใช่ของรหัสสิทธิ์ เพราะตอนบันทึกต้องอ้างถึงแถวนี้ */
          id: String(link.id),
          codeId: String(link.benefitCode),
          text: code?.name || code?.description || `รหัสสิทธิ์ #${link.benefitCode}`,
          claimable: link.hasClaimButton !== false,
          capPerYear: link.perUserLimit ?? null,
          claimedThisYear: 0,
          provisional: false,
        };
      })
    : ui.benefits.map((b) => ({
        id: b.code,
        codeId: b.code,
        text: b.text,
        /* ถอยมาใช้ active_benefit_codes ซึ่งไม่มีค่านี้ — ถือว่าไม่มีปุ่มไว้ก่อน
           ปลอดภัยกว่าบอกว่ามีปุ่มทั้งที่อาจปิดอยู่ */
        claimable: false,
        capPerYear: null,
        claimedThisYear: 0,
        provisional: false,
      }));

  return {
    /* id ของ backend — เพิ่ม ลบ และตั้งชื่ออะไรก็ได้โดยไม่สับสนระดับ */
    id: String(ui.id),
    name: ui.name,
    minSpend: ui.minSpend,
    maxSpend: ui.maxSpend,
    benefits,
    /* ⛔ CRM ไม่มีภาพประกอบระดับ
       จำนวนสมาชิกมาจาก loyalty-tier-summary แยกต่างหาก ส่งเข้าหน้าจอทาง prop memberCounts
       ไม่ได้ยัดลงฟิลด์นี้เพราะ endpoint คนละตัวและอาจล้มเหลวแยกกัน */
    imageUrl: null,
    imageAlt: '',
    memberCount: 0,
    provisional: false,
  };
}

/** ใช้ตอนยังไม่ได้ต่อ backend — ให้ /foundations และโหมดสาธิตยังดูได้ */
export const MOCK_TIER_FALLBACK = MOCK_TIERS;
